/**
 * Check setup state per repo folder (module level, like runStore: it
 * survives leaving the project page).
 *
 *   report    the last report (null until the first check answered)
 *   checking  a check is in flight
 *   open      the checklist panel is expanded
 *   pending   [{ repo, config }] a Start / Run all held back by problems ("Start anyway")
 *   offerRun  every safe fix ran: offer "Run all" in the panel
 *   terminal  the user opened this folder in the Terminal: the next check skips the cache
 *             (a toolchain installed by hand changes no repo file)
 */
import { create } from 'zustand';
import { checkSetup } from './doctorApi';

export const useDoctorStore = create(() => ({ repos: {} }));

const seqs = new Map();
const EMPTY = Object.freeze({ report: null, checking: false, open: false, pending: null, offerRun: false, error: null, terminal: false });

export const repoDoctor = (s, path) => s.repos[path] || EMPTY;

export function patchDoctor(path, fields) {
  useDoctorStore.setState((s) => ({ repos: { ...s.repos, [path]: { ...(s.repos[path] || EMPTY), ...fields } } }));
}

/**
 * Check `configs` of the repo at `path`. Resolves to the report, or null when
 * the check is unavailable (no desktop, an error): a check that cannot run
 * never blocks a start. Only the newest check per repo lands.
 */
export async function runCheck(path, configs, { force = false } = {}) {
  const seq = (seqs.get(path) || 0) + 1;
  seqs.set(path, seq);
  const forced = force || repoDoctor(useDoctorStore.getState(), path).terminal;
  patchDoctor(path, { checking: true, error: null, terminal: false });
  try {
    const report = await checkSetup(path, configs, forced);
    if (seqs.get(path) !== seq) return report || null;
    const valid = report && Array.isArray(report.items) ? report : null;
    patchDoctor(path, { report: valid, checking: false });
    return valid;
  } catch (e) {
    if (seqs.get(path) === seq) patchDoctor(path, { checking: false, error: String(e?.message || e) });
    return null;
  }
}

/** The user opened this folder (or one inside it) in the Terminal. */
export function markTerminalOpened(path) {
  patchDoctor(path, { terminal: true });
}

export function resetDoctorStoreForTests() {
  seqs.clear();
  useDoctorStore.setState({ repos: {} });
}
